import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Divider, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Chip, IconButton, Button, Breadcrumbs, Link, CircularProgress, Alert
} from '@mui/material';
import { 
    ArrowBack, Receipt, Person, Store, LocationOn, 
    Payment, AccessTime, Info 
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getFoodBookingById } from '../../../services/Api';

const ViewFoodBookingDetails = () => {
    const navigate = useNavigate();

    const { id } = useParams();
    
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBookingDetails = async () => {
            setLoading(true);
            try {
                const response = await getFoodBookingById(id);
                if (response.success || response.data) {
                    setBooking(response.data || response);
                } else {
                    setError(response.message || 'Failed to fetch booking details');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Error occurred while fetching details');
            } finally {
                setLoading(false);
            }
        };

        // Always fetch to get the freshest data, even if state exists, 
        // to ensure we have all deep nested relational data like items and addresses.
        fetchBookingDetails();
    }, [id]);

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'delivered') return 'success';
        if (s === 'cancelled' || s === 'rejected') return 'error';
        if (s === 'pending' || s === 'processing') return 'warning';
        return 'default';
    };

    if (loading && !booking) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !booking) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBack />} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    if (!booking) return null;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/bookings/food/all'); }}>
                        Food Bookings
                    </Link>
                    <Typography color="text.primary">Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            Booking #{booking.bookingNumber || booking.id}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                            <Chip 
                                label={(booking.status || 'Pending').toUpperCase()} 
                                color={getStatusColor(booking.status)} 
                                size="small" 
                                sx={{ fontWeight: 900, borderRadius: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 16 }} />
                                Placed on: {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column: People & Places */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Person color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Customer Details</Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{booking.user?.name || 'Unknown'}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{booking.user?.mobile || '-'}</Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <LocationOn sx={{ color: 'text.secondary', mt: 0.5 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Delivery Address</Typography>
                                    {booking.address ? (
                                        <>
                                            <Typography variant="caption" color="primary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                                                {booking.address.addressType || 'Home'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {booking.address.street}, {booking.address.area}<br />
                                                {booking.address.city}, {booking.address.state} - {booking.address.pincode}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No address provided</Typography>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Store color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Vendor Details</Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{booking.foodVendor?.shopName || 'Unknown Vendor'}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{booking.foodVendor?.mobile || '-'}</Typography>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Vendor Location</Typography>
                            <Typography variant="body2">
                                {booking.foodVendor?.addresses?.[0]?.addressLine1 || '-'}<br />
                                {booking.foodVendor?.addresses?.[0]?.addressLine2}<br />
                                {booking.foodVendor?.addresses?.[0]?.city} {booking.foodVendor?.addresses?.[0]?.state ? `, ${booking.foodVendor?.addresses?.[0]?.state}` : ''}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Order Items & Finances */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Receipt color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Order Items</Typography>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Item Description</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {booking.items?.length > 0 ? (
                                            booking.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.menuItemName || 'Item'}</Typography>
                                                        {item.menuItemVariant && (
                                                            <Typography variant="caption" color="text.secondary">Variant: {item.menuItemVariant}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">{item.quantity}x</TableCell>
                                                    <TableCell align="right">₹{item.unitPrice}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{item.totalPrice}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                    <Typography color="text.secondary">No items found for this order.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Payment color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Bill Summary</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Items Total</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.itemsTotal || '0.00'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">GST Amount</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.gstAmount || '0.00'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Delivery Charges</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.deliveryCharge || '0.00'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Service Charges</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.serviceCharge || '0.00'}</Typography>
                                </Box>
                                
                                <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Grand Total</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>₹{booking.grandTotal || '0.00'}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Info color="action" />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Status</Typography>
                                    <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 900, color: booking.paymentStatus?.toLowerCase() === 'paid' ? 'success.main' : 'warning.main' }}>
                                        {booking.paymentStatus || 'Pending'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewFoodBookingDetails;
